import { z } from 'zod';

/**
 * Proactive "this bank transaction looks like an entry you already posted"
 * suggestions.
 *
 * Match-to-existing already exists in the match drawer; what was missing is the
 * suggestion. Post a journal entry by hand, and when the bank transaction turns
 * up days later the feed should offer to link the two rather than quietly
 * inviting you to post a duplicate.
 *
 * (Owned by the auto-match work — extend freely.)
 */

/** How far back to look for an already-posted entry. */
export const EXISTING_ENTRY_MATCH_DAYS = 60;

export const existingEntryMatchSchema = z.object({
  journalEntryId: z.string().uuid(),
  entryNumber: z.string(),
  date: z.string(),
  memo: z.string().nullable(),
  source: z.string(),
  /** Signed movement on the bank's GL account, as a decimal string. */
  signedMovement: z.string(),
  /** Whole dollars of difference from the bank amount; 0 = exact. */
  amountDelta: z.string(),
  /** Absolute day gap between the entry date and the transaction date. */
  dayGap: z.number(),
  /** 0.00–1.00. Amount agreement dominates; date proximity breaks ties. */
  confidence: z.number(),
  /** One short sentence explaining the match, shown under the suggestion. */
  reason: z.string(),
});
export type ExistingEntryMatch = z.infer<typeof existingEntryMatchSchema>;

export const bankTransactionSuggestionSchema = z.object({
  bankTransactionId: z.string().uuid(),
  /** Best candidates, highest confidence first. */
  matches: z.array(existingEntryMatchSchema),
});
export type BankTransactionSuggestion = z.infer<typeof bankTransactionSuggestionSchema>;

export const listExistingEntryMatchesQuerySchema = z.object({
  /**
   * Score only these transactions. The feed sends the ids on the current page
   * so a suggestion is available for every visible row regardless of paging.
   * Omit to scan the company's unmatched transactions (newest first, `limit`).
   */
  transactionIds: z.array(z.string().uuid()).max(250).optional(),
  /** Restrict to one bank account; omit for every account in the company. */
  bankAccountId: z.string().uuid().optional(),
  /** Only return suggestions at or above this confidence. */
  minConfidence: z.coerce.number().min(0).max(1).default(0.5),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type ListExistingEntryMatchesQuery = z.infer<typeof listExistingEntryMatchesQuerySchema>;

/**
 * At or above this confidence the feed shows the suggestion inline, without the
 * user opening the match drawer. Below it, the suggestion is still returned but
 * is presented as a "possible" match rather than a one-click action.
 */
export const EXISTING_ENTRY_HIGH_CONFIDENCE = 0.8;
